import {
  MANUAL_TERM_SOURCE,
  commentUpdateValue,
  isEpisodeExcluded,
  normalizeTermKey,
  normalizeTermValue,
  type EpisodeTerm,
  type EpisodeReviewResp,
  type EpisodeSummary,
  type EpisodeReviewUpdate,
} from "@/api";

/**
 * 에피소드 전역 리뷰 편집 draft 의 순수 코어.
 *
 * 리뷰(제외 · 결과 · 결함 · 표기 · 결함 수용 · 코멘트)는 데이터셋별 오버레이가 아니라 **에피소드
 * 전역** 속성이다. 이 모듈은 그 편집 draft 의 shape 과 순수 전이만 소유한다. React·서버 호출·목록
 * 상태는 소유하지 않는다.
 *
 * 결과(`task_failed`)와 결함(`quality_tags`)은 서로 독립이다. 어느 축도 다른 축에서 파생되지 않는다.
 *
 * Owns:
 * - 단일 에피소드 draft 의 shape 과 초기값, 순수 리듀서.
 * - 저장값 대비 바뀐 필드 판정. 그 판정으로 저장 payload 를 만들고, 409 뒤 입력을 최신 값 위에 다시 얹는다.
 * - 응답 → row 병합.
 *
 * Does not own:
 * - 에피소드 목록 로딩·선택·다음 이동, 이탈 가드 UI.
 * - 서버 호출과 낙관적 동시성(annotation_version). 소비처가 소유한다.
 * - 쓰기 필드의 null·생략 의미(`@/api` 의 `episodeReview.ts`).
 */

// 패널의 로컬 편집(저장 전). 서버 커밋은 소비처가 payload 로 조립해 보낸다.
export type EpisodeReviewDraft = {
  excluded: boolean;
  task_failed: boolean;
  quality_tags: readonly string[];
  /**
   * 자유 어휘. 서버가 준 값을 **부여 경로와 함께** 보관한다.
   *
   * 서버는 저장 경로 자기 행(`manual`)만 바꾼다. 그래서 폼이 지울 수 있는 것은 `manual` 뿐이다.
   * 전체를 보관하면 화면에서 그 규칙을 지키기 쉽다.
   */
  terms: readonly EpisodeTerm[];
  accepted_invalid: boolean;
  comment: string;
};

type DraftField = keyof EpisodeReviewDraft;

// 활성 에피소드의 저장값 → 패널 draft 초기값. 전환/저장 후 draft 리셋에 쓴다.
export function draftFromRow(row: EpisodeSummary): EpisodeReviewDraft {
  return {
    excluded: isEpisodeExcluded(row.status),
    task_failed: row.task_failed,
    quality_tags: [...row.quality_tags],
    terms: [...row.terms],
    accepted_invalid: row.accepted_invalid,
    comment: row.review_comment ?? "",
  };
}

// 순수 리듀서. 소비처가 자신의 상태 컨테이너에 결과를 반영한다(서버 호출 없음).
export function withExcluded(draft: EpisodeReviewDraft, excluded: boolean): EpisodeReviewDraft {
  return { ...draft, excluded };
}

/**
 * 실패 표시를 켜거나 끈다. 결과는 기본 성공에서 시작하고, 사람은 실패만 표시한다. 그래서 켜진
 * 표시를 다시 누르면 기본 성공으로 돌아간다. 결함·코멘트는 바꾸지 않는다.
 */
export function withToggledFailure(draft: EpisodeReviewDraft): EpisodeReviewDraft {
  return { ...draft, task_failed: !draft.task_failed };
}

// 결함 수용을 켜거나 끈다. 수용할 수 있는지(검증 무효)는 소비처가 저장값으로 판정한다.
export function withToggledAcceptance(draft: EpisodeReviewDraft): EpisodeReviewDraft {
  return { ...draft, accepted_invalid: !draft.accepted_invalid };
}

export function withToggledTag(draft: EpisodeReviewDraft, tag: string): EpisodeReviewDraft {
  const tags = draft.quality_tags;
  const next = tags.includes(tag) ? tags.filter((t) => t !== tag) : [...tags, tag];
  return { ...draft, quality_tags: next };
}

/**
 * 어휘 한 건을 더한다. 키는 `normalizeTermKey` 로 정규화하고, 값은 앞뒤 공백만 제거한다.
 *
 * 값 정규화는 서버가 한다. 화면이 미리 정규화하면 사용자가 입력한 표기가 사라진다. 키가 규칙
 * 밖이거나 값이 비면 draft 를 그대로 돌려준다. 같은 `(키, 값 정규화형)` 행이 이미 있으면 더하지
 * 않는다. 다른 경로가 붙인 행도 같은 어휘로 본다.
 */
export function withTermAdded(
  draft: EpisodeReviewDraft,
  termKey: string,
  termValue: string,
): EpisodeReviewDraft {
  const key = normalizeTermKey(termKey);
  const value = termValue.trim();
  if (!key.ok || value === "") {
    return draft;
  }
  const added: EpisodeTerm = {
    term_key: key.key,
    term_value: value,
    // 온톨로지 항은 화면이 정하지 않는다. 카탈로그에 있는 쌍일 때만 서버가 연결한다.
    // 없으면 미매핑으로 남고, 큐레이션 큐가 처리한다.
    canonical_key: null,
    canonical_value: null,
    source: MANUAL_TERM_SOURCE,
  };
  const identity = termIdentity(added);
  if (draft.terms.some((term) => termIdentity(term) === identity)) {
    return draft;
  }
  return { ...draft, terms: [...draft.terms, added] };
}

/**
 * 어휘 한 건을 지운다. **다른 경로가 붙인 행은 지우지 않는다.** 폼이 지울 수 없는 행을 지운
 * 것처럼 보이면 안 된다.
 */
export function withTermRemoved(
  draft: EpisodeReviewDraft,
  removed: EpisodeTerm,
): EpisodeReviewDraft {
  if (removed.source !== MANUAL_TERM_SOURCE) {
    return draft;
  }
  const identity = termIdentity(removed);
  const terms = draft.terms.filter(
    (term) => term.source !== MANUAL_TERM_SOURCE || termIdentity(term) !== identity,
  );
  return { ...draft, terms };
}

export function withComment(draft: EpisodeReviewDraft, comment: string): EpisodeReviewDraft {
  return { ...draft, comment };
}

/**
 * draft 가 저장값과 다른 필드 목록.
 *
 * 태그는 **집합**으로 비교한다. 서버는 저장 시 순서를 재정렬한다. 순서만 다른 것을 바뀐 것으로
 * 보면 저장 직후에도 "저장 안 됨"이 남는다. 코멘트는 쓰기 값과 같은 규칙(앞뒤 공백 제거, 공백만 =
 * 없음)으로 비교한다. 그래서 공백만 넣었다 지운 것은 변경으로 세지 않는다.
 */
function changedFields(draft: EpisodeReviewDraft, row: EpisodeSummary): readonly DraftField[] {
  const saved = draftFromRow(row);
  const changed: DraftField[] = [];
  if (draft.excluded !== saved.excluded) changed.push("excluded");
  if (draft.task_failed !== saved.task_failed) changed.push("task_failed");
  if (!sameTagSet(draft.quality_tags, saved.quality_tags)) changed.push("quality_tags");
  if (!sameTermSet(draft.terms, saved.terms)) changed.push("terms");
  if (draft.accepted_invalid !== saved.accepted_invalid) changed.push("accepted_invalid");
  if (commentUpdateValue(draft.comment) !== commentUpdateValue(saved.comment)) {
    changed.push("comment");
  }
  return changed;
}

/** 저장하지 않은 편집이 있는가. 즉 이 draft 가 활성 행의 저장값과 다른가. */
export function isDraftDirty(draft: EpisodeReviewDraft, row: EpisodeSummary): boolean {
  return changedFields(draft, row).length > 0;
}

/**
 * 저장할 코멘트의 글자 수. 보낼 값(`commentUpdateValue`, 앞뒤 공백 제거)으로 잰다. 코드 포인트
 * 단위라 서버 `max_length` 와 같은 단위다.
 */
export function commentSaveLength(comment: string): number {
  return Array.from(commentUpdateValue(comment) ?? "").length;
}

/**
 * 저장하면 한도를 넘는 코멘트를 보내는가.
 *
 * 코멘트를 바꾸지 않았으면 payload 에 싣지 않으므로 막지 않는다. 한도가 줄기 전에 저장된 긴 코멘트가
 * 다른 축의 저장과 검토 완료까지 막으면 안 된다.
 */
export function commentExceedsLimit(
  draft: EpisodeReviewDraft,
  row: EpisodeSummary,
  maxLength: number,
): boolean {
  return (
    changedFields(draft, row).includes("comment") && commentSaveLength(draft.comment) > maxLength
  );
}

/**
 * 어휘가 같은가. `(키, 값 정규화형)` 집합으로 비교한다.
 *
 * 저장 직후 응답의 원문은 사전에 먼저 등록된 표기로 바뀔 수 있다(`gori` 를 보냈는데 `Gori` 가
 * 돌아옴). 원문으로 비교하면 저장하자마자 다시 dirty 가 된다.
 */
function sameTermSet(left: readonly EpisodeTerm[], right: readonly EpisodeTerm[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  const seen = new Set(right.map(termIdentity));
  return left.every((term) => seen.has(termIdentity(term)));
}

// 어휘 한 건의 정체. 키는 이미 정규화된 값이다(서버 응답 또는 `withTermAdded`).
function termIdentity(term: EpisodeTerm): string {
  return `${term.term_key}\u0000${normalizeTermValue(term.term_value)}`;
}

function sameTagSet(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  const seen = new Set(right);
  return left.every((tag) => seen.has(tag));
}

/**
 * draft → 저장·검토 완료 payload. `base` 는 편집을 시작한 저장값이다.
 *
 * 항상 검토 완료(`complete_review: true`)를 보낸다. 리뷰 화면에는 저장과 완료를 나누는 동작이
 * 없다. 바뀐 필드만 담는다. 바뀌지 않은 필드를 다시 보내면, 그 사이 다른 곳에서 바뀐 값을
 * 이 화면의 옛 값으로 되돌린다.
 */
export function toReviewPayload(
  draft: EpisodeReviewDraft,
  base: EpisodeSummary,
): EpisodeReviewUpdate {
  const payload: EpisodeReviewUpdate = {
    expected_annotation_version: base.annotation_version,
    complete_review: true,
  };
  for (const field of changedFields(draft, base)) {
    switch (field) {
      case "excluded":
        payload.excluded = draft.excluded;
        break;
      case "task_failed":
        payload.task_failed = draft.task_failed;
        break;
      case "quality_tags":
        payload.quality_tags = [...draft.quality_tags];
        break;
      case "terms":
        payload.terms = draft.terms.map((term) => ({
          term_key: term.term_key,
          term_value: term.term_value,
          canonical_key: term.canonical_key,
          canonical_value: term.canonical_value,
        }));
        break;
      case "accepted_invalid":
        payload.accepted_invalid = draft.accepted_invalid;
        break;
      case "comment":
        payload.comment = commentUpdateValue(draft.comment);
        break;
    }
  }
  return payload;
}

/**
 * 409 뒤 입력을 최신 값 위에 다시 얹는다. 사용자가 바꾼 필드(`oldBase` 대비)는 입력값을 지킨다.
 * 바꾸지 않은 필드는 `latest` 값을 쓴다.
 *
 * 병합이 아니다. 저장은 하지 않고 draft 만 바꾼다. 사용자는 최신 값을 확인한 뒤 다시 저장한다.
 * 바꾸지 않은 필드까지 옛 값으로 남기면, 다음 저장이 다른 곳의 변경을 조용히 되돌린다.
 */
export function rebaseDraft(
  draft: EpisodeReviewDraft,
  oldBase: EpisodeSummary,
  latest: EpisodeSummary,
): EpisodeReviewDraft {
  const edited = new Set(changedFields(draft, oldBase));
  const fresh = draftFromRow(latest);
  return {
    excluded: edited.has("excluded") ? draft.excluded : fresh.excluded,
    task_failed: edited.has("task_failed") ? draft.task_failed : fresh.task_failed,
    quality_tags: edited.has("quality_tags") ? draft.quality_tags : fresh.quality_tags,
    terms: edited.has("terms") ? draft.terms : fresh.terms,
    accepted_invalid: edited.has("accepted_invalid")
      ? draft.accepted_invalid
      : fresh.accepted_invalid,
    comment: edited.has("comment") ? draft.comment : fresh.comment,
  };
}

// 저장 응답(authoritative)으로 목록 row 를 갱신한다. 요청값을 낙관적으로 반영하지 않고 응답을
// 쓴다. 검토자·시각·스냅샷·`annotation_version` 도 응답값으로 갱신한다. 그래서 머리 줄의 검토 상태와
// 다음 저장 토큰이 최신이 된다.
export function rowFromResponse(row: EpisodeSummary, resp: EpisodeReviewResp): EpisodeSummary {
  return {
    ...row,
    status: resp.status,
    validation_status: resp.validation_status,
    invalid_reason: resp.invalid_reason,
    task_failed: resp.task_failed,
    quality_tags: resp.quality_tags,
    terms: resp.terms,
    accepted_invalid: resp.accepted_invalid,
    accepted_invalid_snapshot: resp.accepted_invalid_snapshot,
    accepted_invalid_stale: resp.accepted_invalid_stale,
    review_comment: resp.review_comment,
    review_comment_snapshot: resp.review_comment_snapshot,
    review_comment_stale: resp.review_comment_stale,
    reviewed_by_member: resp.reviewed_by_member,
    reviewed_at: resp.reviewed_at,
    annotation_version: resp.annotation_version,
  };
}
